import type { Zone } from '@seek/audience-zones';
import type { SearchParams, Source } from '@seek/chalice-types';
import type { Locale } from '@seek/melways-sites';

import getCountryFromZone from 'src/config/utils/getCountryFromZone';
import clean from 'src/modules/clean-object';
import { convertToApiQuery } from 'src/modules/seek-jobs-search-query';
import { shouldRequestRelatedSearches } from 'src/modules/seek-jobs-search-query/convertToApiQueryHelper';
import type { Country } from 'src/types/globals';

import { getSource } from './getSource';

// This function builds the API query parameters for the job search API
// The function is shared across /search, /count and /counts.
export const buildApiQuery = ({
  baseKeywords,
  country,
  facets,
  locale,
  newSince,
  queryHints,
  pathname,
  prevPathname,
  relatedSearchesCount,
  searchQuery,
  seekerId,
  sessionId,
  solId,
  source: sourceParam,
  stateToken,
  userClientId,
  userQueryId,
  zone,
}: {
  baseKeywords?: string;
  country: Country;
  facets?: string;
  locale: Locale;
  newSince?: number;
  queryHints?: string;
  pathname: string;
  prevPathname: string | null;
  relatedSearchesCount?: number;
  searchQuery: SearchParams;
  seekerId?: number;
  sessionId: string;
  solId: string;
  source?: Source;
  stateToken?: string;
  userClientId: string;
  userQueryId?: string;
  zone: Zone;
}) => {
  const tags = searchQuery.tags;
  const analyticsParams = {
    eventCaptureSessionId: sessionId || null,
    userid: userClientId || null,
    userqueryid: userQueryId || null,
    usersessionid: userClientId || null,
  };

  // TODO-ZONES: See if we can update the library so we can pass in Zone
  // instead of country value
  const searchParams = convertToApiQuery({
    country,
    searchQuery,
    zone,
  });

  const isLocationOnlySerp = Boolean(
    !searchQuery.keywords && !searchQuery.classification && searchQuery.where,
  );

  const isLocationAndWorkTypeSerp = Boolean(
    isLocationOnlySerp && searchQuery.worktype,
  );

  const requestFacets = isLocationOnlySerp || isLocationAndWorkTypeSerp;

  const source = getSource({
    savedSearchId: searchQuery?.savedsearchid,
    source: sourceParam,
    pathname,
    prevPathname,
  });

  const cleanApiQuery = clean({
    facets: requestFacets ? 'distinctTitle' : '',
    newSince,
    siteKey: `${getCountryFromZone(zone)}-Main`,
    sourcesystem: 'houston',
    stateToken,
    ...analyticsParams,
    ...{
      ...searchParams,
      baseKeywords,
    },
    ...{
      tags,
    },
    locale,
    seekerId,
    solId,
    source,
    ...(shouldRequestRelatedSearches({ searchParams, zone }) && {
      relatedSearchesCount,
    }),
    ...(queryHints && { queryHints }),
    ...(facets && { facets }),
  });

  return {
    ...cleanApiQuery,
  };
};
